import React from 'react';
import { ScheduleItem, Type } from '../../../api/responses';
import { formatTime } from './utils';
import {
  TimeBadge,
  ScheduleItemContainer,
  ScheduleItemContent,
  RoundDescription,
  Attention,
  Line,
} from './styles';
import ScheduleTableDesktop from './components/ScheduleTableDesktop';
import ScheduleTableMobile from './components/ScheduleTableMobile';

type TScheduleList = {
  scheduleItems: ScheduleItem[];
  competitionType: Type;
  lang: string;
  rounds?: Array<{ name: string; description: string }>;
};

const ScheduleList = ({
  scheduleItems,
  competitionType,
  lang = 'ru',
  rounds = [],
}: TScheduleList) => {
  const getRoundDescription = (roundName: string | null): string => {
    if (!roundName) return '';
    const round = rounds.find((r) => r.name === roundName);

    const team =
      competitionType === 'team'
        ? lang === 'ru'
          ? 'Турнир по парной управленческой борьбе. '
          : 'Team game competition. '
        : '';
    const quick =
      competitionType === 'quick'
        ? lang === 'ru'
          ? 'Турнир по быстрой управленческой борьбе. '
          : 'Quick game competition. '
        : '';
    const classic =
      competitionType === 'classic'
        ? lang === 'ru'
          ? 'Турнир по классической управленческой борьбе. '
          : 'Classic game competition. '
        : '';

    return `${team || classic || quick}${round?.description}` || '';
  };

  const renderSchedule = () => {
    const elements: JSX.Element[] = [];
    let currentGameItems: ScheduleItem[] = [];
    let tableCount = 0;

    const flushGameTable = (shouldAddLine = true) => {
      if (currentGameItems.length > 0) {
        elements.push(
          <React.Fragment key={`game-table-${currentGameItems[0].id}`}>
            <ScheduleTableDesktop
              gameItems={currentGameItems}
              competitionType={competitionType}
            />
            <ScheduleTableMobile
              gameItems={currentGameItems}
              competitionType={competitionType}
              lang={lang}
            />
          </React.Fragment>
        );

        if (shouldAddLine) {
          elements.push(<Line key={`line-after-${currentGameItems[0].id}`} />);
        }

        tableCount++;
        currentGameItems = [];
      }
    };

    const sortedItems = [...scheduleItems].sort((a, b) => a.order - b.order);

    const nonGameItems = sortedItems.filter(
      (item) => item.item_type !== 'game'
    );
    const lastNonGameItemId =
      nonGameItems.length > 0
        ? nonGameItems[nonGameItems.length - 1]?.id
        : null;

    sortedItems.forEach((item, index) => {
      const nextItem = sortedItems[index + 1];
      const hasTableAfter = nextItem?.item_type === 'game';

      if (item.item_type === 'intro') {
        flushGameTable(true);

        const roundDescription = getRoundDescription(item.round_name);
        if (roundDescription) {
          elements.push(
            <RoundDescription key={`round-desc-${item.id}`}>
              {roundDescription}
            </RoundDescription>
          );

          if (item.round_name === 'Round 1' && competitionType === 'quick') {
            elements.push(
              <Attention key={`attention-${item.id}`}>
                {lang === 'ru'
                  ? 'ВНИМАНИЕ! СОСТАВ СУДЕЙСКИХ КОЛЛЕГИЙ МОЖЕТ БЫТЬ ИЗМЕНЁН!'
                  : 'Notice: Voting panel members may change'}
              </Attention>
            );
          }
        }

        const isLastNonGame = item.id === lastNonGameItemId;
        const shouldShowBorder = !hasTableAfter && !isLastNonGame;

        elements.push(
          <ScheduleItemContainer key={item.id} $isLast={!shouldShowBorder}>
            <TimeBadge>
              {formatTime(item.start_time)} - {formatTime(item.end_time)}
            </TimeBadge>
            <ScheduleItemContent>{item.item_type_display}</ScheduleItemContent>
          </ScheduleItemContainer>
        );
      } else if (item.item_type === 'game') {
        currentGameItems.push(item);
      } else {
        flushGameTable(true);

        const isLastNonGame = item.id === lastNonGameItemId;
        const shouldShowBorder = !hasTableAfter && !isLastNonGame;

        elements.push(
          <ScheduleItemContainer key={item.id} $isLast={!shouldShowBorder}>
            <TimeBadge>
              {formatTime(item.start_time)} - {formatTime(item.end_time)}
            </TimeBadge>
            <ScheduleItemContent>{item.item_type_display}</ScheduleItemContent>
          </ScheduleItemContainer>
        );
      }
    });

    flushGameTable(false);

    return elements;
  };

  return <div style={{ width: '100%' }}>{renderSchedule()}</div>;
};

export default ScheduleList;
